import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { syncNotificationPreferencesFromProfile } from "../utils/notificationPreferences";

const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);

  // Function to fetch profile data
  const fetchProfile = async (requireAuth = true) => {
    const token = localStorage.getItem('token');
    
    // If requireAuth is false, don't fetch profile
    if (!requireAuth) {
      setIsProfileLoaded(true);
      return;
    }

    // If no token and auth is required, set as not loaded
    if (!token && requireAuth) {
      setIsProfileLoaded(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Using token for profile fetch:', token ? `${token.substring(0, 20)}...` : 'No token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Try GET first, then fallback to POST if needed
      let response;
      try {
        response = await axios.get('/users/profile', config);
        console.log('GET request successful');
      } catch (getError) {
        console.log('GET request failed, trying POST:', getError.message);
        response = await axios.post('/profile', {}, config);
        console.log('POST request successful');
      }

      console.log('=== PROFILE API DEBUG ===');
      console.log('Full response:', response);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Response data keys:', Object.keys(response.data || {}));
      console.log('Response data structure:', JSON.stringify(response.data, null, 2));
      console.log('=== END DEBUG ===');      
      
      if (response?.status === 200) {
        console.log('Setting profile data:', response.data);
        setProfileData(response.data);
        if (response.data?.data?.notificationPreferences) {
          syncNotificationPreferencesFromProfile(response.data.data.notificationPreferences);
        }
        setIsProfileLoaded(true);
        console.log('Profile data set successfully');
      } else {
        console.log('Unexpected response status:', response.status);
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setError(err);
      setIsProfileLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Function to clear profile data (useful for logout)
  const clearProfile = () => {
    setProfileData(null);
    setError(null);
    setIsProfileLoaded(false);
  };

  // Function to update profile data locally
  const updateProfileData = (newData) => {
    setProfileData(prev => {
      const next = { ...prev, ...newData };
      // Deep merge data object to avoid losing profile fields
      if (newData?.data) {
        next.data = prev?.data ? { ...prev.data, ...newData.data } : newData.data;
      }
      return next;
    });
  };

  // Auto-fetch profile when component mounts if token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchProfile(true);
    } else {
      setIsProfileLoaded(true);
    }
  }, []);

  return (
    <ProfileContext.Provider 
      value={{ 
        profileData, 
        loading, 
        error, 
        isProfileLoaded,
        fetchProfile,
        clearProfile,
        updateProfileData
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};



